/*
 *  signin.js
 *  Project: Kanoogi Platform - Kanoogi Sign In
 *
 *  Created by Chris Taylor on Nov 5, 2023
 *  Copyright 2023-2026 Kanoogi Inc. | All Rights Reserved.
 *
 */

"use strict";

const kanoogi=window.kanoogi||{};									// Create one Kanoogi object to contain the whole shooting match
window.kanoogi=kanoogi;												// Store the master Kanoogi object into the global window object

import './style.css';
import './styleanim.css';
import './signin.css';

import * as Module_utils from '../../Kanlibs/KAN_Utils.js';
import * as network from '../../Kanlibs/KAN_Network.js';

import * as signedin from './signedin.js';
import * as optionpanel from './optionpanel.js';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { ResolveSigninDestination } from './signinDestination.js';

const utils=kanoogi.utils||new Module_utils.KAN_Utils();			// Initialize a new utils module if it doesn't exist
window.kanoogi.utils=utils;											// Store the master Kanoogi utils object into the global window object

kanoogi.network=new network.KAN_Network();							// Init Kanoogi Networking

const local=utils.RunningLocally();

const DebugOnly=utils.DebugOnly;

if (local)															// Testing the code locally
   console.log("Webpage is running in local testing environment");

const emID=document.getElementById('emailaddress');
const pwID=document.getElementById('password');
const lmID=document.getElementById('loginmess');
const moreLoginOptionsID=document.getElementById('more_login_options');
const moreLoginOptionsPanelID=document.getElementById('more_login_options_panel');
const googleOneTapID=document.getElementById('google_onetap_login');

const urlParams=new URLSearchParams(location.search);
const SIGNIN_NEXT_KEY='kanoogi:signin-next';
kanoogi.baseurl=ResolveSigninDestination(urlParams.get('next'),window.location.origin);
try
	{
	if (kanoogi.baseurl!=='/home')
		sessionStorage.setItem(SIGNIN_NEXT_KEY,kanoogi.baseurl);
	else
		sessionStorage.removeItem(SIGNIN_NEXT_KEY);
	}
catch (_) {}
const GOOGLE_CLIENT_ID_FALLBACK='119032227426-9v723dlq3uq4em81urq53f2gprpmivjj.apps.googleusercontent.com';
const GOOGLE_ONE_TAP_ENABLED=true;
const GOOGLE_ONE_TAP_DISABLED_KEY='kanoogi:disable-google-one-tap';
const LAST_LOGIN_PROVIDER_KEY='kanoogi:last-login-provider';
/* Google is deliberately pointed at our own tile rather than #google_login.
   That div is owned by google.accounts.id.renderButton() — it is a centered
   flex row holding Google's iframe, so a badge appended there became a second
   flex child sitting beside the button at the page's base font size, well
   outside the tile type scale, and landed underneath the One Tap card. */
const LAST_LOGIN_PROVIDERS=
	{
	google:'google_onetap_login',
	apple:'apple_login',
	discord:'discord_login',
	github:'github_login',
	twitch:'twitch_login',
	passkey:'passkey_login'
	};
let googleSigninInitialized=false;

/* ---------------------------------------------------------------- */

function CompleteSigninRedirect()
	{
	try { sessionStorage.removeItem(SIGNIN_NEXT_KEY); }
	catch (_) {}
	window.location.href=kanoogi.baseurl;
	}
/* ---------------------------------------------------------------- */

function RememberLastLoginProvider(provider)
	{
	try { localStorage.setItem(LAST_LOGIN_PROVIDER_KEY,provider); }
	catch (_) {}
	}
/* ---------------------------------------------------------------- */

function ReadLastLoginProvider()
	{
	try { return(localStorage.getItem(LAST_LOGIN_PROVIDER_KEY)||''); }
	catch (_) { return(''); }
	}
/* ---------------------------------------------------------------- */

function ResolveLastLoginAnchor(provider)
	{
	const button=document.getElementById(LAST_LOGIN_PROVIDERS[provider]);
	if (button && !button.hidden) return(button);

	/* One Tap is switched off, so our Google tile is hidden and the only
	   Google affordance left is the button Google renders for itself. */
	if (provider==='google')
		{
		const rendered=document.getElementById('google_login');
		if (rendered && rendered.children.length) return(rendered);
		}

	return(null);
	}
/* ---------------------------------------------------------------- */

function RenderLastLoginBadge()
	{
	document.querySelectorAll('.last-login-badge').forEach(badge=>badge.remove());
	document.querySelectorAll('.last-login-provider').forEach(button=>button.classList.remove('last-login-provider'));

	const provider=ReadLastLoginProvider();
	const providerID=LAST_LOGIN_PROVIDERS[provider];
	if (!providerID) return;

	const button=ResolveLastLoginAnchor(provider);
	if (!button) return;
	if (moreLoginOptionsPanelID && moreLoginOptionsPanelID.contains(button))
		SetMoreLoginOptionsVisible(true);

	button.classList.add('last-login-provider');
	const badge=document.createElement('span');
	badge.className='last-login-badge';
	badge.textContent='Last used';
	button.appendChild(badge);
	}
/* ---------------------------------------------------------------- */

function SetMoreLoginOptionsVisible(visible)
	{
	if (!moreLoginOptionsID || !moreLoginOptionsPanelID) return;

	moreLoginOptionsPanelID.hidden=!visible;
	moreLoginOptionsID.setAttribute('aria-expanded',visible?'true':'false');
	moreLoginOptionsID.textContent=visible?'Fewer options':'More options';
	}
/* ---------------------------------------------------------------- */

export function ToggleMoreLoginOptions()
	{
	if (!moreLoginOptionsPanelID) return;
	SetMoreLoginOptionsVisible(moreLoginOptionsPanelID.hidden);
	}
/* ---------------------------------------------------------------- */

function GoogleOneTapEnabled()
	{
	if (!GOOGLE_ONE_TAP_ENABLED) return(false);
	const queryValue=(urlParams.get('onetap')||'').toLowerCase();
	if (queryValue==='0' || queryValue==='false' || queryValue==='off')
		return(false);

	try { return(localStorage.getItem(GOOGLE_ONE_TAP_DISABLED_KEY)!=='true'); }
	catch (_) { return(true); }
	}
/* ---------------------------------------------------------------- */

function SyncGoogleOneTapButton()
	{
	if (!googleOneTapID) return;
	googleOneTapID.hidden=!GoogleOneTapEnabled();
	}
/* ---------------------------------------------------------------- */

function PromptGoogleOneTap(showMessage=false)
	{
	if (!GoogleOneTapEnabled()) return;

	try
		{
		google.accounts.id.prompt(notification=>
			{
			if (!notification) return;
			if (notification.isNotDisplayed && notification.isNotDisplayed())
				{
				DebugOnly(`Google One Tap not displayed:${notification.getNotDisplayedReason?notification.getNotDisplayedReason():'unknown'}`);
				if (showMessage) LoginMessage('Google One Tap is not available right now.');
				}
			else
			if (notification.isSkippedMoment && notification.isSkippedMoment())
				{
				DebugOnly(`Google One Tap skipped:${notification.getSkippedReason?notification.getSkippedReason():'unknown'}`);
				if (showMessage) LoginMessage('Google One Tap was skipped for now.');
				}
			});
		}
	catch (err)
		{
		DebugOnly(`Google One Tap prompt failed:${err.message||err}`);
		if (showMessage) LoginMessage('Google One Tap is currently unavailable.');
		}
	}
/* ---------------------------------------------------------------- */

export function ShowGoogleOneTap()
	{
	if (local)
		{
		LoginMessage('Google One Tap is available on the live site.');
		return;
		}

	if (!GoogleOneTapEnabled())
		{
		LoginMessage('Google One Tap is disabled in this browser.');
		return;
		}

	if (!googleSigninInitialized || !window.google || !window.google.accounts || !window.google.accounts.id)
		{
		LoginMessage('Google One Tap is still loading.');
		return;
		}

	PromptGoogleOneTap(true);
	}
/* ---------------------------------------------------------------- */

// EmailValid() - Returns the emailaddress itself if valid, null if not
// NOTE: Assumes the value is emID.value

function EmailValid()
	{
	const emailaddress=emID.value.toLowerCase().trim();				// Trim any nonsense off the new email address

	if (emailaddress.length && utils.VerifyEmailAddress(emailaddress))
		return(emailaddress);

	return(null);
	}
/* ---------------------------------------------------------------- */

// LoginMessage() - Display message on the login screen

function LoginMessage(message)
	{
	lmID.textContent=message;

	lmID.style.visibility='visible';
	lmID.classList.remove('glowing');
	void lmID.offsetWidth;											// Clever and important trick to reset the animation
	lmID.classList.add('glowing');
	}
/* ---------------------------------------------------------------- */

// CredentialResponse() - Callback that receives the response back from Google OAuth2 sign in system
// NOTE: I break it out here as a function for readability, nothing more

async function CredentialResponse(payload)
	{
	const response=await fetch('/verify',
		{
		method:'GET',
		headers:
			{
			'Content-Type':'application/json',
			'IDToken':payload.credential,
			'X-Kanoogi-Auth-Flow':'create'
			}
		});
	const json=await response.json();

	if (json.jwt)
		{
		RememberLastLoginProvider('google');
		signedin.SetJWT(json.jwt);
		CompleteSigninRedirect();
		}
	else
	if (json.status==='pending' && json.token)
		{
		RememberLastLoginProvider('google');
		window.location.href=`/finish?token=${encodeURIComponent(json.token)}`;
		}
	else
		{
		const messages=
			{
			no_account:'No Kanoogi account exists for that Google email yet.',
			email_missing:'Google did not return an email for this sign in.',
			email_not_verified:'Google did not return a verified email for this sign in.',
			invalid_token:'Google sign in could not be verified.'
			};
		LoginMessage(messages[json.reason]||'Google sign in failed.');
		}
	}
/* ---------------------------------------------------------------- */

async function GoogleClientId()
	{
	try
		{
		const response=await fetch('/oauth/google/clientid',{method:'GET',headers:{'Content-Type':'application/json'}});
		if (response.ok)
			{
			const json=await response.json();
			if (json.clientId) return(json.clientId);
			}
		}
	catch (_) {}

	return(GOOGLE_CLIENT_ID_FALLBACK);
	}
/* ---------------------------------------------------------------- */

async function InitGoogleSignin(retries=20)
	{
	if (local) return;												// Don't bother with Google login, doesn't work locally

	if (!window.google || !window.google.accounts || !window.google.accounts.id)
		{
		if (retries>0)
			{
			setTimeout(()=>{InitGoogleSignin(retries-1);},250);
			return;
			}

		LoginMessage('Google sign in is currently unavailable');
		return;
		}

	const clientId=await GoogleClientId();
	const googleLogin=document.getElementById("google_login");
	const googleWidth=Math.round(googleLogin.getBoundingClientRect().width)||288;

	google.accounts.id.initialize(
		{
		client_id:clientId,
		callback:CredentialResponse,
		context:'signin',
		itp_support:true,
		use_fedcm_for_prompt:true
		});
	googleSigninInitialized=true;

	google.accounts.id.renderButton(googleLogin,{theme:"outline",size:"large",width:googleWidth});	// Can be customized
	PromptGoogleOneTap();
	RenderLastLoginBadge();
	}
/* ---------------------------------------------------------------- */

window.addEventListener('load',()=>{InitGoogleSignin();});
/* ---------------------------------------------------------------- */

export async function Signin()
	{
	const hashword=await utils.EncodeSha256(pwID.value);
	const address=EmailValid();

	if (address)
		{
		// const data=await SendPost('/login',{'ea':address,'hw':hashword});
		const resp=await kanoogi.network.FetchObj('/login',{'ea':address,'hw':hashword});
		if (resp)
			{
			if (resp.token)
				{
				RememberLastLoginProvider('password');
				signedin.SetJWT(resp.token);
				CompleteSigninRedirect();
				}
			else
			if (resp.status==='check_email')						// Incomplete account: server emailed a magic link to finish setup
				LoginMessage(resp.message||"We emailed you a link to finish setting up your account.  Check your inbox.");
			else
			if (resp.verified && resp.verified=='false')
				LoginMessage("Account not yet verified, check email");
			else
				LoginMessage("Incorrect Email or Password");
			}
		}
	else
		LoginMessage('Not a valid email address');
	}
/* ---------------------------------------------------------------- */

export async function PasskeySignin()
	{
	if (!browserSupportsWebAuthn())
		{
		LoginMessage('This browser does not support passkeys.');
		return;
		}

	try
		{
		const optionsResponse=await fetch('/passkeys/login/options',
			{
			method:'GET',
			headers:{'Content-Type':'application/json'}
			});
		if (!optionsResponse.ok)
			{
			LoginMessage('Passkey sign in is currently unavailable.');
			return;
			}

		const optionsJSON=await optionsResponse.json();
		const response=await startAuthentication({optionsJSON:optionsJSON});
		const verifyResponse=await fetch('/passkeys/login/verify',
			{
			method:'POST',
			headers:{'Content-Type':'application/json'},
			body:JSON.stringify(response)
			});
		const json=await verifyResponse.json();

		if (verifyResponse.ok && json.token)
			{
			RememberLastLoginProvider('passkey');
			signedin.SetJWT(json.token);
			CompleteSigninRedirect();
			return;
			}

		LoginMessage(json.error||'Passkey sign in failed.');
		}
	catch (err)
		{
		if (err.name==='NotAllowedError')
			LoginMessage('Passkey sign in was cancelled.');
		else
			LoginMessage('Passkey sign in failed.');
		}
	}
/* ---------------------------------------------------------------- */

async function SigninWithCodes()
	{
	const la=urlParams.get('la');
	const lc=urlParams.get('lc');

	if (la && lc)
		{
		DebugOnly('la:'+la+' lc:'+lc);

		const data=await kanoogi.network.FetchObj('/login',{la:la,lc:lc});

		if (data.token)
			{
			RememberLastLoginProvider('email');
			signedin.SetJWT(data.token);
			// window.location.href=kanoogi.baseurl;
			window.location.href='/notice?success=signin'
			return(true);
			}
		window.location.href='/notice?err=nflccode'
		}

	return(false);
	}
/* ---------------------------------------------------------------- */

export async function ForgotPassword()
	{
	const address=EmailValid();

	if (address)
		{
		const response=await fetch(`/genlogin?ea=${address}`,{method:'GET',headers:{'Content-Type':'application/json'}});
		LoginMessage("Reset request sent");

		const {status}=await response.json()
		if (status) LoginMessage(status);
		}
	else
		LoginMessage("Invalid email address");
	}
/* ---------------------------------------------------------------- */

// TogglePassword() - Flips the state of the text input from password to text, or text to password

 function TogglePassword()
 	{
    const passwordInput=document.getElementById('password');
	passwordInput.type=passwordInput.type==='password'?'text':'password';
    }
/* ---------------------------------------------------------------- */

function KeyHit(event)
	{
	event.preventDefault();

	if (event.keyCode==13)
		Signin();
	}
/* ---------------------------------------------------------------- */

if (pwID) pwID.addEventListener('keyup',KeyHit);

window.Signin=Signin;
window.PasskeySignin=PasskeySignin;
window.ForgotPassword=ForgotPassword;
window.ToggleMoreLoginOptions=ToggleMoreLoginOptions;
window.ShowGoogleOneTap=ShowGoogleOneTap;

// ContinueToPassword() - Step 1 of the two-step form: reveal the password block
// once we have an email.  Purely presentational - it does NOT ask the server
// whether the address exists, because that would turn the sign-in page into an
// account-enumeration oracle.  Both inputs are in the DOM from first paint so
// password managers still see a complete form; Continue only unhides the second
// half and moves focus there.

export function ContinueToPassword()
	{
	const step=document.getElementById('passwordstep');
	const cont=document.getElementById('continuebutton');
	if (!step) { Signin(); return; }								// markup missing -> behave like the old one-step form

	const email=(emID&&emID.value||'').trim();
	if (!email)
		{
		if (lmID) lmID.textContent='Enter your email address to continue.';
		if (emID) emID.focus();
		return;
		}

	step.hidden=false;
	if (cont) cont.hidden=true;
	if (lmID) lmID.textContent='';
	if (pwID) pwID.focus();
	}
window.ContinueToPassword=ContinueToPassword;

// Enter in the email field should advance the step, not submit an empty password.
if (emID)
	emID.addEventListener('keydown',(e)=>{ if (e.key==='Enter') { e.preventDefault(); ContinueToPassword(); } });

(async()=>
	{
	utils.KanoogiTextLogo();

	if (await SigninWithCodes()) return;							// If codes are provided, use those instead

	await optionpanel.ValidateLogin();								// This is a little silly, because you can't sign in if you're already signed in
	optionpanel.MenuState(true);

	document.getElementById('signin_nav').classList.add('active');
	document.querySelector('.password-toggle-icon').addEventListener('click',TogglePassword);
	lmID.style.visibility='hidden';
	SyncGoogleOneTapButton();
	RenderLastLoginBadge();

	const renderPageID=document.getElementById("renderpage");
	if (renderPageID) renderPageID.style.visibility="visible";
	})();
